import React from 'react';
import { motion } from 'framer-motion';
import { Zap, CheckCircle, ArrowRight, Mail, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const HydropowerIndustryPage: React.FC = () => {
  const systems = [
    {
      title: 'Passive Fish Screens (PSF)',
      description: 'NOAA/WDFW compliant wedgewire screens for turbine and penstock protection',
      link: '/filtration/suction-screens/passive-fish-screens'
    },
    {
      title: 'Self-Cleaning Suction Screens',
      description: 'Automated intake protection for variable debris loads',
      link: '/filtration/suction-screens/self-cleaning'
    },
    {
      title: 'Water Box Assemblies',
      description: 'Custom manifolds and multi-screen arrays for high-flow intakes',
      link: '/filtration/water-intake'
    }
  ];

  const benefits = [
    'Available in configurations meeting NOAA/WDFW fish-screen criteria',
    'Low approach-velocity designs minimize entrainment',
    'Custom manifolds, multi-screen arrays, and high-flow assemblies available',
    'Wedgewire construction maintains <0.2 ft/sec approach velocity',
    'No moving parts for passive screens — minimal maintenance',
    'Self-cleaning options for high-debris environments'
  ];

  const applications = [
    {
      title: 'Hydropower Intakes',
      description: 'Protect turbines and generators from debris and sediment in rivers, lakes, and reservoirs with fish-safe screening.'
    },
    {
      title: 'Dam Penstock Screening',
      description: 'Prevent debris from entering penstocks and causing damage to turbines, valves, and control systems.'
    },
    {
      title: 'Fish-Passage Systems',
      description: 'Maintain regulatory compliance with NOAA/WDFW approach-velocity and open-area criteria for juvenile fish protection.'
    },
    {
      title: 'Canal and River Diversions',
      description: 'Screen large-volume diversions for irrigation, municipal, or industrial use while protecting aquatic life.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Hydropower & Dam Filtration Systems | CTC Waterworks"
        description="CTC self-cleaning suction screens and passive fish screens protect turbines, penstocks, and dam intakes. NOAA/WDFW compliant designs for demanding surface-water applications."
        canonical="https://www.ctcwaterworks.com/industries/hydropower"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-ctc-blue via-blue-700 to-blue-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                    <Zap className="text-white" size={32} />
                  </div>
                  <h1 className="text-5xl font-bold">
                    Hydropower & Dams
                  </h1>
                </div>
                <p className="text-2xl text-blue-100 mb-6 font-light leading-relaxed">
                  Designed for the most demanding surface-water intakes, CTC self-cleaning suction screens protect turbines, penstocks, and downstream systems while meeting fish-screen compliance requirements.
                </p>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
                  <div className="flex items-start">
                    <Shield className="mr-3 text-blue-200 mt-1 flex-shrink-0" size={24} />
                    <p className="text-blue-100 leading-relaxed">
                      Available with configurations meeting NOAA/WDFW fish-screen approach-velocity and open-area criteria — approved for surface-water and dam intake use
                    </p>
                  </div>
                </div>
              </motion.div>

              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>
                <Link
                  to="/contact?topic=hydropower-filtration"
                  className="block w-full bg-ctc-blue text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center shadow-lg mb-4"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  Our team will help you design fish-safe intake screening for your hydropower or dam application
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Why CTC Filtration for Hydropower & Dams?
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Built for the most demanding surface-water applications with regulatory compliance
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <CheckCircle className="mr-3 text-blue-600 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700 leading-relaxed">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Common Applications */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Common Applications
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              CTC filtration systems protect hydropower and dam infrastructure
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {applications.map((app, index) => (
                <div key={index} className="bg-white border-2 border-blue-200 rounded-lg p-8 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Overview */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Recommended Systems
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Choose from passive fish screens or self-cleaning suction screens based on your compliance and debris requirements
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {systems.map((system, index) => (
                <div key={index} className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-6 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{system.title}</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">{system.description}</p>
                  <Link
                    to={system.link}
                    className="flex items-center justify-between bg-white border-2 border-blue-600 text-blue-600 px-4 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors"
                  >
                    <span>Learn More</span>
                    <ArrowRight size={20} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Highlight */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                NOAA/WDFW Fish-Screen Compliance
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Our Passive Screen for Fish (PSF) systems are specifically designed to meet NOAA and Washington Department
                of Fish & Wildlife (WDFW) criteria for approach velocity and open-area requirements. Wedgewire construction
                maintains less than 0.2 ft/sec approach velocity across the screen surface, protecting juvenile fish from
                impingement and entrainment while providing reliable turbine and penstock protection.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">&lt;0.2 ft/sec</p>
                  <p className="text-sm text-gray-600">Approach Velocity</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">NOAA/WDFW</p>
                  <p className="text-sm text-gray-600">Certified Compliant</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">Wedgewire</p>
                  <p className="text-sm text-gray-600">Passive Design</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Specifications Callout */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Custom Arrays for High-Flow Applications
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6 text-center">
                For large hydropower intakes and dam diversions, we design custom multi-screen arrays, manifolds, and
                high-flow assemblies tailored to your site-specific flow rates, debris loads, and regulatory requirements.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white border border-blue-200 rounded-lg px-6 py-3">
                  <p className="text-sm font-semibold text-gray-900">Custom Manifolds</p>
                </div>
                <div className="bg-white border border-blue-200 rounded-lg px-6 py-3">
                  <p className="text-sm font-semibold text-gray-900">Multi-Screen Arrays</p>
                </div>
                <div className="bg-white border border-blue-200 rounded-lg px-6 py-3">
                  <p className="text-sm font-semibold text-gray-900">High-Flow Design</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Hydropower Infrastructure?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Our engineering team will help you design fish-safe intake screening that meets regulatory requirements while protecting your turbines and penstocks.
            </p>
            <Link
              to="/contact?topic=hydropower-filtration"
              className="inline-block bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Talk to a Filtration Expert
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default HydropowerIndustryPage;
